'use client';

import { create } from 'zustand';
import type { StateCreator } from 'zustand';
import { persist, devtools, createJSONStorage } from 'zustand/middleware';
import type { PersistOptions, PersistStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Check if we're running in a browser environment
 */
const isClient = typeof window !== 'undefined';

/**
 * Safe localStorage wrapper that handles SSR environments
 * This prevents hydration mismatches when the server and client render different content
 */
const safeLocalStorage: PersistStorage<unknown> = {
  getItem: (name) => {
    if (!isClient) return null;
    try {
      const value = localStorage.getItem(name);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    if (!isClient) return;
    try {
      localStorage.setItem(name, JSON.stringify(value));
    } catch {
      // Ignore storage errors (e.g., quota exceeded, private browsing)
    }
  },
  removeItem: (name) => {
    if (!isClient) return;
    try {
      localStorage.removeItem(name);
    } catch {
      // Ignore storage errors
    }
  },
};

/**
 * Store registry to track all stores for global reset
 */
type StoreResetFn = () => void;
const storeResetFns = new Set<StoreResetFn>();

/**
 * Creates a Zustand store with common middleware stack:
 * - devtools: For Redux DevTools integration
 * - immer: For immutable state updates with mutable syntax
 *
 * @param name - Store name for devtools
 * @param initializer - State creator function
 */
export function createStore<T>(
  name: string,
  initializer: StateCreator<T, [['zustand/devtools', never], ['zustand/immer', never]], []>
) {
  const store = create<T>()(
    devtools(
      immer(initializer),
      { name, enabled: process.env.NODE_ENV === 'development' }
    )
  );

  // Extract reset function if it exists
  const initialState = store.getState();
  if (typeof (initialState as { reset?: unknown }).reset === 'function') {
    storeResetFns.add((initialState as { reset: StoreResetFn }).reset);
  }

  return store;
}

/**
 * Creates a Zustand store with persistence and common middleware stack:
 * - persist: For localStorage persistence
 * - devtools: For Redux DevTools integration
 * - immer: For immutable state updates with mutable syntax
 *
 * @param name - Store name for devtools and localStorage key
 * @param initializer - State creator function
 * @param persistOptions - Optional persistence configuration overrides
 */
export function createPersistedStore<T>(
  name: string,
  initializer: StateCreator<
    T,
    [['zustand/devtools', never], ['zustand/immer', never], ['zustand/persist', unknown]],
    []
  >,
  persistOptions?: Partial<PersistOptions<T, Partial<T>>>
) {
  const store = create<T>()(
    devtools(
      immer(
        persist(initializer, {
          name: `hr-${name}`,
          storage: safeLocalStorage as unknown as PersistStorage<Partial<T>>,
          skipHydration: !isClient,
          ...persistOptions,
        })
      ),
      { name, enabled: process.env.NODE_ENV === 'development' }
    )
  );

  // Extract reset function if it exists
  const initialState = store.getState();
  if (typeof (initialState as { reset?: unknown }).reset === 'function') {
    storeResetFns.add((initialState as { reset: StoreResetFn }).reset);
  }

  return store;
}

/**
 * Resets all registered stores to their initial state.
 * Typically called on logout to clear all client-side state.
 */
export function resetAllStores(): void {
  storeResetFns.forEach((resetFn) => {
    resetFn();
  });
}

/**
 * Clears all persisted store data from localStorage.
 * Call this when user logs out to ensure clean state.
 */
export function clearPersistedStores(): void {
  if (!isClient) return;

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('hr-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Type helper for creating slice patterns in stores
 */
export type StateSlice<T> = StateCreator<
  T,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  T
>;

/**
 * Type helper for persisted store slices
 */
export type PersistedStateSlice<T> = StateCreator<
  T,
  [['zustand/devtools', never], ['zustand/immer', never], ['zustand/persist', unknown]],
  [],
  T
>;
